import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { 
  Vault, DollarSign, Send, ArrowLeft, Shield, FileText,
  Plus, AlertTriangle, CheckCircle, Clock, ExternalLink, Copy, RefreshCw
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BETH_CONTRACT = "0x6822824c5349bf005e353ec5dda3fa34595ea811";
const TREASURY_WALLET = "0x2FB355B99b2060e42DB50D99393D00f47A41d322";

const PrivateLendingReserve = () => {
  const { token, user } = useAuth();
  const [reserveStats, setReserveStats] = useState({
    totalReserve: 1000000000, // 1 billion BETH
    allocated: 0,
    available: 1000000000,
    pendingTransfers: 0
  });
  const [allocations, setAllocations] = useState([]);
  const [loading, setLoading] = useState(false);
  
  // New allocation form
  const [showNewAllocation, setShowNewAllocation] = useState(false);
  const [newAllocation, setNewAllocation] = useState({
    recipientName: '',
    recipientWallet: '',
    amount: '',
    purpose: 'credit_enhancement',
    debtType: 'securities_finance',
    notes: ''
  });

  useEffect(() => {
    fetchReserveData();
  }, []);

  const fetchReserveData = async () => {
    setLoading(true);
    try {
      // Fetch allocations from backend
      const response = await fetch(`${API}/credit-enhancement/treasury-summary`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setReserveStats(prev => ({
          ...prev,
          allocated: data.total_committed_beth || 0,
          available: 1000000000 - (data.total_committed_beth || 0)
        }));
      }
    } catch (error) {
      console.error('Error fetching reserve data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateAllocation = async () => {
    if (!newAllocation.recipientWallet || !newAllocation.amount) {
      toast.error('Please fill in all required fields');
      return;
    }

    // Validate wallet address
    if (!/^0x[a-fA-F0-9]{40}$/.test(newAllocation.recipientWallet)) {
      toast.error('Invalid wallet address');
      return;
    }

    const amount = parseFloat(newAllocation.amount);
    if (amount <= 0 || amount > reserveStats.available) {
      toast.error('Invalid amount or insufficient reserve');
      return;
    }

    try {
      // Create allocation record
      const allocationRecord = {
        id: `ALLOC-${Date.now()}`,
        ...newAllocation,
        amount: amount,
        status: 'pending',
        createdAt: new Date().toISOString(),
        createdBy: user?.email
      };

      setAllocations(prev => [allocationRecord, ...prev]);
      setReserveStats(prev => ({
        ...prev,
        allocated: prev.allocated + amount,
        available: prev.available - amount,
        pendingTransfers: prev.pendingTransfers + 1
      }));

      toast.success('Allocation created! Use the transfer instructions below to complete the on-chain transfer.');
      setShowNewAllocation(false);
      setNewAllocation({
        recipientName: '',
        recipientWallet: '',
        amount: '',
        purpose: 'credit_enhancement',
        debtType: 'securities_finance',
        notes: ''
      });
    } catch (error) {
      toast.error('Failed to create allocation');
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard');
  };

  const formatNumber = (num) => {
    return new Intl.NumberFormat('en-US').format(num);
  };

  const purposeLabels = {
    credit_enhancement: 'Credit Enhancement',
    pas_settlement: 'PAS Debt Settlement',
    lender_transfer: 'Lender Transfer',
    other: 'Other'
  };

  const debtTypeLabels = {
    securities_finance: 'Securities Finance',
    repo: 'Repo Transaction',
    swaps: 'Swaps',
    structured_credit: 'Structured Credit',
    prime_brokerage: 'Prime Brokerage',
    margin_lending: 'Margin Lending',
    collateral_mgmt: 'Collateral Management',
    credit_facility: 'Credit Facility',
    other: 'Other'
  };

  return (
    <div className="min-h-screen bg-[#0a1628]" data-testid="private-lending-reserve">
      {/* Header */}
      <div className="bg-[#0d1d35] border-b border-[#1a2d4a] px-6 py-4">
        <div className="max-w-7xl mx-auto flex justify-between items-center">
          <div className="flex items-center gap-4">
            <Link to="/admin-dashboard">
              <Button variant="ghost" size="sm" className="text-gray-400 hover:text-white">
                <ArrowLeft className="w-4 h-4 mr-1" />
                Dashboard
              </Button>
            </Link>
            <div>
              <h1 className="text-2xl font-bold text-white flex items-center gap-2">
                <Vault className="w-6 h-6 text-[#b8860b]" />
                Private Lending Reserve
              </h1>
              <p className="text-gray-400 text-sm">Manage BETH allocations for credit enhancements and debt settlements</p>
            </div>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={fetchReserveData} disabled={loading}>
              <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
            <Button className="bg-[#b8860b] hover:bg-[#9a7209]" onClick={() => setShowNewAllocation(true)}>
              <Plus className="w-4 h-4 mr-2" />
              New Allocation
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Reserve Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
          <Card className="bg-gradient-to-br from-[#b8860b]/20 to-[#b8860b]/5 border-[#b8860b]/30">
            <CardContent className="pt-6">
              <div className="flex items-center gap-3 mb-2">
                <Vault className="w-5 h-5 text-[#b8860b]" />
                <p className="text-gray-400 text-sm">Total Reserve</p>
              </div>
              <p className="text-2xl font-bold text-white">{formatNumber(reserveStats.totalReserve)} BETH</p>
              <p className="text-xs text-gray-500 mt-1">1 Billion BETH</p>
            </CardContent>
          </Card>
          <Card className="bg-[#0d1d35] border-[#1a2d4a]">
            <CardContent className="pt-6">
              <div className="flex items-center gap-3 mb-2">
                <Send className="w-5 h-5 text-blue-400" />
                <p className="text-gray-400 text-sm">Allocated</p>
              </div>
              <p className="text-2xl font-bold text-blue-400">{formatNumber(reserveStats.allocated)} BETH</p>
              <p className="text-xs text-gray-500 mt-1">${formatNumber(reserveStats.allocated)} USD value</p>
            </CardContent>
          </Card>
          <Card className="bg-[#0d1d35] border-[#1a2d4a]">
            <CardContent className="pt-6">
              <div className="flex items-center gap-3 mb-2">
                <DollarSign className="w-5 h-5 text-green-400" />
                <p className="text-gray-400 text-sm">Available</p>
              </div>
              <p className="text-2xl font-bold text-green-400">{formatNumber(reserveStats.available)} BETH</p>
              <p className="text-xs text-gray-500 mt-1">${formatNumber(reserveStats.available)} USD value</p>
            </CardContent>
          </Card>
          <Card className="bg-[#0d1d35] border-[#1a2d4a]">
            <CardContent className="pt-6">
              <div className="flex items-center gap-3 mb-2">
                <Clock className="w-5 h-5 text-yellow-400" />
                <p className="text-gray-400 text-sm">Pending Transfers</p>
              </div>
              <p className="text-2xl font-bold text-yellow-400">{reserveStats.pendingTransfers}</p>
              <p className="text-xs text-gray-500 mt-1">Awaiting on-chain execution</p>
            </CardContent>
          </Card>
        </div>

        {/* Treasury Wallet Info */}
        <Card className="bg-[#0d1d35] border-[#1a2d4a] mb-8">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <Shield className="w-5 h-5 text-[#b8860b]" />
              Treasury Wallet
            </CardTitle>
            <CardDescription>Primary wallet holding the BETH reserve for credit enhancements</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-4 p-4 bg-[#1a2d4a]/50 rounded-lg">
              <div className="flex-1">
                <p className="text-xs text-gray-500 mb-1">Wallet Address</p>
                <p className="text-white font-mono text-sm">{TREASURY_WALLET}</p>
              </div>
              <Button variant="ghost" size="sm" onClick={() => copyToClipboard(TREASURY_WALLET)}>
                <Copy className="w-4 h-4" />
              </Button>
              <a href={`https://etherscan.io/address/${TREASURY_WALLET}`} target="_blank" rel="noopener noreferrer">
                <Button variant="ghost" size="sm">
                  <ExternalLink className="w-4 h-4" />
                </Button>
              </a>
            </div>
            <div className="mt-4 p-4 bg-blue-900/20 border border-blue-500/30 rounded-lg">
              <p className="text-blue-400 text-sm font-medium mb-2">BETH Token Contract</p>
              <div className="flex items-center gap-2">
                <p className="text-white font-mono text-sm">{BETH_CONTRACT}</p>
                <Button variant="ghost" size="sm" onClick={() => copyToClipboard(BETH_CONTRACT)}>
                  <Copy className="w-4 h-4" />
                </Button>
                <a href={`https://etherscan.io/token/${BETH_CONTRACT}`} target="_blank" rel="noopener noreferrer">
                  <Button variant="ghost" size="sm">
                    <ExternalLink className="w-4 h-4" />
                  </Button>
                </a>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* New Allocation Form */}
        {showNewAllocation && (
          <Card className="bg-[#0d1d35] border-[#b8860b]/50 mb-8">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <Plus className="w-5 h-5 text-[#b8860b]" />
                Create New BETH Allocation
              </CardTitle>
              <CardDescription>Allocate BETH tokens for credit enhancement or debt settlement</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-gray-400 mb-2">Recipient Name / Entity *</label>
                  <Input 
                    placeholder="e.g., ABC Mortgage Company"
                    value={newAllocation.recipientName}
                    onChange={(e) => setNewAllocation(prev => ({ ...prev, recipientName: e.target.value }))}
                    className="bg-[#1a2d4a] border-[#2a3d5a] text-white"
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-400 mb-2">Recipient Wallet Address *</label>
                  <Input 
                    placeholder="0x..."
                    value={newAllocation.recipientWallet}
                    onChange={(e) => setNewAllocation(prev => ({ ...prev, recipientWallet: e.target.value }))}
                    className="bg-[#1a2d4a] border-[#2a3d5a] text-white font-mono"
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-400 mb-2">Amount (BETH) *</label>
                  <Input 
                    type="number"
                    placeholder="e.g., 100000"
                    value={newAllocation.amount}
                    onChange={(e) => setNewAllocation(prev => ({ ...prev, amount: e.target.value }))}
                    className="bg-[#1a2d4a] border-[#2a3d5a] text-white"
                  />
                  <p className="text-xs text-gray-500 mt-1">Available: {formatNumber(reserveStats.available)} BETH</p>
                </div>
                <div>
                  <label className="block text-sm text-gray-400 mb-2">Purpose</label>
                  <select 
                    value={newAllocation.purpose}
                    onChange={(e) => setNewAllocation(prev => ({ ...prev, purpose: e.target.value }))}
                    className="w-full p-2 bg-[#1a2d4a] border border-[#2a3d5a] text-white rounded-md"
                  >
                    {Object.entries(purposeLabels).map(([value, label]) => (
                      <option key={value} value={value}>{label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm text-gray-400 mb-2">Transaction Type</label>
                  <select 
                    value={newAllocation.debtType}
                    onChange={(e) => setNewAllocation(prev => ({ ...prev, debtType: e.target.value }))}
                    className="w-full p-2 bg-[#1a2d4a] border border-[#2a3d5a] text-white rounded-md"
                  >
                    {Object.entries(debtTypeLabels).map(([value, label]) => (
                      <option key={value} value={value}>{label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm text-gray-400 mb-2">Notes</label>
                  <Input 
                    placeholder="Additional details..."
                    value={newAllocation.notes}
                    onChange={(e) => setNewAllocation(prev => ({ ...prev, notes: e.target.value }))}
                    className="bg-[#1a2d4a] border-[#2a3d5a] text-white"
                  />
                </div>
              </div>

              <div className="flex gap-3 mt-6">
                <Button className="bg-[#b8860b] hover:bg-[#9a7209]" onClick={handleCreateAllocation}>
                  <CheckCircle className="w-4 h-4 mr-2" />
                  Create Allocation
                </Button>
                <Button variant="outline" onClick={() => setShowNewAllocation(false)}>
                  Cancel
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Transfer Instructions */}
        <Card className="bg-yellow-900/20 border-yellow-500/30 mb-8">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-yellow-400" />
              On-Chain Transfer Instructions
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-gray-300 mb-4">
              To transfer BETH to a lender or counterparty wallet, follow these steps:
            </p>
            <ol className="space-y-3 text-gray-300">
              <li className="flex gap-3">
                <span className="text-[#b8860b] font-bold">1.</span>
                <span>Go to <a href={`https://etherscan.io/address/${BETH_CONTRACT}#writeContract`} target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:underline">BETH Token Write Contract</a></span>
              </li>
              <li className="flex gap-3">
                <span className="text-[#b8860b] font-bold">2.</span>
                <span>Connect your Treasury wallet ({TREASURY_WALLET.slice(0, 10)}...)</span>
              </li>
              <li className="flex gap-3">
                <span className="text-[#b8860b] font-bold">3.</span>
                <span>Use the <code className="bg-gray-800 px-2 py-1 rounded">transfer</code> function:</span>
              </li>
              <li className="pl-6 bg-[#1a2d4a]/50 p-3 rounded font-mono text-sm">
                <p className="text-gray-400">to: <span className="text-white">[recipient wallet address]</span></p>
                <p className="text-gray-400">amount: <span className="text-white">[amount with 18 decimals]</span></p>
                <p className="text-xs text-gray-500 mt-2">Example: 1,000,000 BETH = 1000000000000000000000000</p>
              </li>
              <li className="flex gap-3">
                <span className="text-[#b8860b] font-bold">4.</span>
                <span>Click "Write" and confirm in MetaMask</span>
              </li>
            </ol>
          </CardContent>
        </Card>

        {/* Recent Allocations */}
        <Card className="bg-[#0d1d35] border-[#1a2d4a]">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <FileText className="w-5 h-5 text-[#b8860b]" />
              Recent Allocations
            </CardTitle>
          </CardHeader>
          <CardContent>
            {allocations.length === 0 ? (
              <div className="text-center py-12 text-gray-500">
                <Vault className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p>No allocations yet. Create one to get started.</p>
              </div>
            ) : (
              <div className="space-y-4">
                {allocations.map((alloc) => (
                  <div key={alloc.id} className="p-4 bg-[#1a2d4a]/50 rounded-lg">
                    <div className="flex justify-between items-start">
                      <div>
                        <p className="text-white font-medium">{alloc.recipientName}</p>
                        <p className="text-sm text-gray-400 font-mono">{alloc.recipientWallet}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-white font-bold">{formatNumber(alloc.amount)} BETH</p>
                        <span className={`text-xs px-2 py-1 rounded ${
                          alloc.status === 'completed' ? 'bg-green-900/50 text-green-400' :
                          alloc.status === 'pending' ? 'bg-yellow-900/50 text-yellow-400' :
                          'bg-gray-800 text-gray-400'
                        }`}>
                          {alloc.status.toUpperCase()}
                        </span>
                      </div>
                    </div>
                    <div className="flex gap-4 mt-3 text-xs text-gray-500">
                      <span>{purposeLabels[alloc.purpose]}</span>
                      <span>•</span>
                      <span>{debtTypeLabels[alloc.debtType]}</span>
                      <span>•</span>
                      <span>{new Date(alloc.createdAt).toLocaleDateString()}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default PrivateLendingReserve;
